import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { canonicalUrl, absUrl, canonicalLink, geoMeta } from "@/lib/seo";
import { useState } from "react";
import { ArrowLeft, Check, Plus, ShoppingCart } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { useQuoteCart } from "@/lib/quote-cart";
import { hardwareProducts, getHardwareBySlug, type HardwareProduct } from "@/lib/hardware-products";

export const Route = createFileRoute("/shop/hardware/$slug")({
  loader: ({ params }) => {
    const product = getHardwareBySlug(params.slug);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.product;
    if (!p) return { links: [canonicalLink(`/shop/hardware/${params.slug}`)], meta: [
        ...geoMeta(),{ property: "og:url", content: canonicalUrl(`/shop/hardware/${params.slug}`) },{ title: "Hardware not found | Texas Made Shade" }] };
    const title = `${p.name} — ${p.size} | Texas Made Shade`;
    const description = `${p.blurb} Add to your order and we'll email a written quote you can pay online. Ask about eradicated awnings, backlit awnings and back lite awnings.`;
    return {
      links: [canonicalLink(`/shop/hardware/${p.slug}`)],
      meta: [
        ...geoMeta(),{ property: "og:url", content: canonicalUrl(`/shop/hardware/${p.slug}`) },
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: absUrl(p.image) },
        { name: "twitter:image", content: absUrl(p.image) },
      ],
    };
  },
  component: HardwareDetailPage,
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
      <SiteHeader />
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-bold uppercase text-navy-deep">Hardware not found</h1>
        <p className="mt-3 text-navy-deep/70">That hardware item isn't in our catalog.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-red">
          <ArrowLeft className="h-4 w-4" /> Back to Shop
        </Link>
      </section>
      <SiteFooter />
    </main>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
        <SiteHeader />
        <section className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-bold uppercase text-navy-deep">Something went wrong</h1>
          <p className="mt-3 text-navy-deep/70">{(error as Error)?.message}</p>
          <button
            type="button"
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-red"
          >
            Retry
          </button>
        </section>
        <SiteFooter />
      </main>
    );
  },
});

function HardwareDetailPage() {
  const { product } = Route.useLoaderData();
  const { addItem, items, open: openCart } = useQuoteCart();
  const [finish, setFinish] = useState<string>(product.finishes[0] ?? "");
  const [size, setSize] = useState<string>(product.sizes[0] ?? "");
  const [justAdded, setJustAdded] = useState(false);

  const inCart = items.some((i) => i.productId === product.id);

  const handleAdd = () => {
    addItem({
      productId: product.id,
      name: product.name,
      size: `${size} · ${finish}`,
      category: `Hardware · ${product.manufacturer}`,
      sourceUrl: `/shop/hardware/${product.slug}`,
    });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  // Related products: other hardware families
  const related: HardwareProduct[] = hardwareProducts.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
      <SiteHeader />

      <section className="border-b-2 border-navy-deep/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-6 py-3 font-display text-[11px] uppercase tracking-wider text-navy-deep/70 lg:px-8">
          <Link to="/shop" className="hover:text-red">Shop Marine Hardware &amp; Snaps</Link>
          <span>/</span>
          <span className="text-navy-deep/70">Hardware</span>
          <span>/</span>
          <span className="text-navy-deep">{product.manufacturer}</span>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-8 lg:py-14">
        <Link
          to="/shop"
          className="mb-6 inline-flex items-center gap-2 font-display text-[11px] uppercase tracking-wider text-navy-deep/70 hover:text-red"
        >
          <ArrowLeft className="h-4 w-4" /> Back to all hardware
        </Link>

        <div className="grid gap-10 lg:grid-cols-2">
          {/* Image */}
          <div className="border-2 border-navy-deep/10 bg-white p-6 shadow-card">
            <div className="aspect-square w-full overflow-hidden bg-cream/40">
              <img
                src={product.image}
                alt={`${product.name} — ${product.size}`}
                loading="eager"
                className="h-full w-full object-contain"
              />
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-red">
              {product.manufacturer}
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold uppercase leading-tight text-navy-deep md:text-4xl">
              {product.name}
            </h1>
            <div className="mt-2 font-display text-lg text-navy-deep/80">{product.size}</div>

            <p className="mt-5 text-base leading-relaxed text-navy-deep/80">{product.blurb}</p>

            {product.productNote && (
              <div className="mt-4 border-l-4 border-red bg-cream/60 p-3 text-sm text-navy-deep/80">
                <strong className="font-display uppercase tracking-wider text-[11px] text-red">Note:</strong>{" "}
                {product.productNote}
              </div>
            )}

            {/* Finish + size pickers */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="font-display text-[11px] uppercase tracking-wider text-navy-deep">
                  Finish
                </label>
                <select
                  value={finish}
                  onChange={(e) => setFinish(e.target.value)}
                  className="mt-1 w-full border-2 border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep focus:border-navy-deep focus:outline-none"
                >
                  {product.finishes.map((f: any) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-display text-[11px] uppercase tracking-wider text-navy-deep">
                  Size / Configuration
                </label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="mt-1 w-full border-2 border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep focus:border-navy-deep focus:outline-none"
                >
                  {product.sizes.map((s: any) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleAdd}
                className={`inline-flex items-center gap-2 px-6 py-3.5 font-display text-sm uppercase tracking-wider text-white shadow-bold transition ${
                  inCart || justAdded ? "bg-red hover:bg-navy-deep" : "bg-navy-deep hover:bg-red"
                }`}
              >
                {justAdded ? (
                  <><Check className="h-5 w-5" /> Added to Order</>
                ) : inCart ? (
                  <><Plus className="h-5 w-5" /> Add Another</>
                ) : (
                  <><Plus className="h-5 w-5" /> Add to Order</>
                )}
              </button>
              <button
                type="button"
                onClick={openCart}
                className="inline-flex items-center gap-2 border-2 border-navy-deep px-6 py-3.5 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                <ShoppingCart className="h-5 w-5" /> View My Order ({items.length})
              </button>
            </div>

            <p className="mt-3 text-xs text-navy-deep/60">
              No payment now. We'll email a written quote and a QuickBooks invoice you can pay online.
            </p>

            {/* Features */}
            {product.features.length > 0 && (
              <div className="mt-8">
                <h2 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
                  Key Features
                </h2>
                <ul className="mt-3 space-y-2">
                  {product.features.map((f: any) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-navy-deep/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-red" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Description + specs */}
        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">Description</h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-navy-deep/80">
              {product.description.map((para: any, i: number) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          <aside className="border-2 border-navy-deep/10 bg-white p-5 shadow-card">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
              Specifications
            </h2>
            <dl className="mt-3 divide-y divide-navy-deep/10 text-sm">
              {product.specs.map((s: any) => (
                <div key={s.label} className="flex justify-between gap-4 py-2">
                  <dt className="text-navy-deep/60">{s.label}</dt>
                  <dd className="text-right font-medium text-navy-deep">{s.value}</dd>
                </div>
              ))}
            </dl>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 border-t-2 border-navy-deep/10 pt-10">
            <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
              Other hardware
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to="/shop/hardware/$slug"
                  params={{ slug: r.slug }}
                  className="group flex gap-4 border-2 border-navy-deep/10 bg-white p-4 transition hover:border-navy-deep hover:shadow-bold"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden bg-cream/40">
                    <img src={r.image} alt={r.name} loading="lazy" className="h-full w-full object-contain" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[10px] uppercase tracking-widest text-red">
                      {r.manufacturer}
                    </div>
                    <div className="mt-0.5 font-display text-sm font-bold uppercase leading-tight text-navy-deep">
                      {r.name}
                    </div>
                    <div className="mt-0.5 text-xs text-navy-deep/70">{r.size}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <LetsTalkForm />
      <SiteFooter />
    </main>
  );
}
